import { Link } from 'react-router-dom';
import { Download, Map, ShoppingBag, Package, TrendingUp, FileText } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEntitlements } from '../../hooks/useEntitlements';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useEffect, useState } from 'react';
import OnboardingModal from '../../components/OnboardingModal';

export default function Dashboard() {
  const { user, userData } = useAuth();
  const { entitlements, loading: entitlementsLoading } = useEntitlements();
  const [filesCount, setFilesCount] = useState(0);
  const [loadingFiles, setLoadingFiles] = useState(true);

  // Calculate journey progress (4 stages total)
  // For now, using simple entitlement count as proxy
  // TODO: Map specific product IDs to stages
  const completedStages = Math.min(entitlements.length, 4);
  const progressPercentage = (completedStages / 4) * 100;

  useEffect(() => {
    // Count total files available from all entitled products
    const countFiles = async () => {
      if (entitlements.length === 0) {
        setFilesCount(0);
        setLoadingFiles(false);
        return;
      }

      try {
        const productIds = entitlements.map(e => e.productId);
        const productsQuery = query(
          collection(db, 'products'),
          where('__name__', 'in', productIds.slice(0, 10)) // Firestore 'in' limit is 10
        );
        const productsSnap = await getDocs(productsQuery);

        let totalFiles = 0;
        productsSnap.forEach(doc => {
          const product = doc.data();
          totalFiles += (product.files?.length || 0);
        });

        setFilesCount(totalFiles);
      } catch (error) {
        console.error('Error counting files:', error);
      } finally {
        setLoadingFiles(false);
      }
    };

    countFiles();
  }, [entitlements]);

  return (
    <div className="space-y-8">
      <OnboardingModal />

      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {userData?.name || user?.displayName || 'there'}!
        </h1>
        <p className="mt-2 text-gray-600">
          Here's your business legal toolkit dashboard
        </p>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* My Products */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">My Products</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {entitlementsLoading ? '...' : entitlements.length}
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Journey Progress */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Journey Progress</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {completedStages}/4
              </p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Available Downloads */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Downloads</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {loadingFiles ? '...' : filesCount}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Download Your Files */}
          <Link
            to="/portal/downloads"
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="bg-blue-50 rounded-lg p-3 inline-block mb-3 group-hover:bg-blue-100 transition-colors">
                  <Download className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Download Your Files
                </h3>
                <p className="text-sm text-gray-600">
                  Access all your legal templates and documents
                </p>
              </div>
            </div>
          </Link>

          {/* View Your Journey */}
          <Link
            to="/portal/journey"
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-green-300 transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="bg-green-50 rounded-lg p-3 inline-block mb-3 group-hover:bg-green-100 transition-colors">
                  <Map className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  View Your Journey
                </h3>
                <p className="text-sm text-gray-600">
                  Track your progress through the 4 business stages
                </p>
              </div>
            </div>
          </Link>

          {/* Unlock More Tools */}
          <Link
            to="/portal/products"
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-purple-300 transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="bg-purple-50 rounded-lg p-3 inline-block mb-3 group-hover:bg-purple-100 transition-colors">
                  <ShoppingBag className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Unlock More Tools
                </h3>
                <p className="text-sm text-gray-600">
                  Explore legal packs for your next business stage
                </p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Business Journey Preview */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Your Business Journey</h2>
            <p className="text-sm text-gray-600 mt-1">
              {completedStages === 0 && 'Just getting started? Begin with the foundation.'}
              {completedStages === 1 && 'Foundation complete! Ready for your first revenue.'}
              {completedStages === 2 && 'Growing! Time to build your team.'}
              {completedStages === 3 && 'Almost there! Protect and scale your business.'}
              {completedStages === 4 && 'All stages complete! You\'re legally protected.'}
            </p>
          </div>
          <Link
            to="/portal/journey"
            className="text-blue-600 hover:text-blue-700 font-medium text-sm whitespace-nowrap"
          >
            View full journey →
          </Link>
        </div>

        {/* Mini progress visualization */}
        <div className="flex items-center space-x-4">
          {[1, 2, 3, 4].map((stage) => (
            <div key={stage} className="flex-1">
              <div
                className={`h-2 rounded-full ${
                  stage <= completedStages
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
              />
              <p className="text-xs text-gray-600 mt-1 text-center">
                Stage {stage}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Empty state for no products */}
      {entitlements.length === 0 && !entitlementsLoading && (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No products yet
          </h3>
          <p className="text-gray-600 mb-6">
            Start your legal journey by exploring our products
          </p>
          <Link
            to="/portal/products"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Browse Products
          </Link>
        </div>
      )}
    </div>
  );
}
